import { useState } from "react";
import { Link } from "react-router-dom";
import { STATUS_BADGE, MATCH_LABEL, playerName, profileNow } from "./scannerLabels";

// Full evidence for one player + approve with an editable school.
export const ResultDetailModal = ({ result, onClose, onApply, onDismiss, onPickProfile, busy }) => {
  const [status, setStatus] = useState(result.status === "Committed" ? "committed" : "uncommitted");
  const [school, setSchool] = useState(result.committedSchool || result.player?.committedTo || "");
  const open = result.review?.state === "open";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-jp-line bg-jp-card p-6 text-sm text-jp-ink space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-jp-ink">{result.playerName || playerName(result.player)}</h3>
            <a
              href={`https://x.com/${result.twitterUsername}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sky-600 dark:text-sky-300 hover:text-jp-accent text-xs"
            >
              @{result.twitterUsername}
            </a>
            {result.currentSchool && <span className="text-xs text-jp-muted"> · {result.currentSchool}</span>}
          </div>
          <button onClick={onClose} className="text-jp-muted hover:text-jp-ink text-xl leading-none">×</button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {result.status && (
            <span className={`px-2 py-0.5 rounded-full border text-xs ${STATUS_BADGE[result.status]}`}>{result.status}</span>
          )}
          {result.committedSchool && <span className="text-jp-ink font-medium">{result.committedSchool}</span>}
          {result.confidence > 0 && <span className="text-xs text-jp-muted">{result.confidence}% sure</span>}
        </div>

        <div className="rounded-xl bg-jp-section p-3 space-y-1">
          <div className="text-xs uppercase tracking-wide text-jp-muted">JUCO Pipeline profile</div>
          {result.player ? (
            <div className="flex flex-wrap items-center gap-2">
              <Link to={`/admin/player/${result.player._id}`} className="text-sky-600 dark:text-sky-300 hover:text-jp-accent font-medium">
                {playerName(result.player)}
              </Link>
              {result.player.team?.name && <span className="text-jp-muted">· {result.player.team.name}</span>}
              <span className="text-jp-muted">· {profileNow(result.player)}</span>
              <span className="text-xs text-jp-muted">({MATCH_LABEL[result.matchMethod]})</span>
            </div>
          ) : (
            <div className="text-amber-700 dark:text-amber-300">Not matched to a profile yet.</div>
          )}
          <button onClick={onPickProfile} className="text-xs text-sky-600 dark:text-sky-300 hover:text-jp-accent">
            {result.player ? "Wrong player? Pick a different profile" : "Pick the profile"}
          </button>
        </div>

        {result.needsReviewReason && (
          <p className="text-amber-700 dark:text-amber-200"><span className="text-jp-muted">Why it needs review: </span>{result.needsReviewReason}</p>
        )}
        {result.evidenceText && (
          <div>
            <div className="text-xs uppercase tracking-wide text-jp-muted">Evidence{result.evidenceType ? ` — ${result.evidenceType}` : ""}</div>
            <blockquote className="mt-1 border-l-2 border-jp-line pl-3 italic">{result.evidenceText}</blockquote>
            {result.evidenceUrl && (
              <a href={result.evidenceUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-sky-600 dark:text-sky-300 hover:text-jp-accent">
                Open the post on X
              </a>
            )}
          </div>
        )}
        {result.timelineSummary && <p><span className="text-jp-muted">Recruiting path: </span>{result.timelineSummary}</p>}
        {result.reasoning && <p className="text-jp-muted"><span className="text-jp-muted">Reasoning: </span>{result.reasoning}</p>}
        {result.timeline?.length > 0 && (
          <ul className="space-y-1 text-xs">
            {result.timeline.map((t, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-jp-muted w-20 shrink-0">{t.eventDate || "no date"}</span>
                <span className="text-jp-muted">{t.eventType}{t.school ? ` — ${t.school}` : ""}</span>
              </li>
            ))}
          </ul>
        )}

        {open ? (
          <div className="border-t border-jp-line pt-4 space-y-3">
            <div className="text-xs uppercase tracking-wide text-jp-muted">Update the profile</div>
            <div className="flex flex-col sm:flex-row gap-2">
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="h-9 rounded-lg bg-jp-section border border-jp-line px-2 text-jp-ink"
              >
                <option value="committed">Committed</option>
                <option value="uncommitted">Uncommitted</option>
              </select>
              {status === "committed" && (
                <input
                  value={school}
                  onChange={(e) => setSchool(e.target.value)}
                  placeholder="School committed to"
                  className="flex-1 h-9 rounded-lg bg-jp-section border border-jp-line px-3 text-jp-ink placeholder:text-jp-muted"
                />
              )}
            </div>
            <div className="flex gap-2">
              <button
                disabled={busy || !result.player || (status === "committed" && !school.trim())}
                onClick={() => onApply({ commitmentStatus: status, committedTo: school.trim() })}
                className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white font-medium"
              >
                Approve — update profile
              </button>
              <button
                disabled={busy}
                onClick={onDismiss}
                className="px-4 py-2 rounded-lg bg-jp-section hover:bg-jp-line disabled:opacity-40 text-jp-ink"
              >
                Dismiss
              </button>
            </div>
          </div>
        ) : (
          <p className="border-t border-jp-line pt-4 text-jp-muted">
            {result.review?.state === "applied"
              ? `Applied: ${result.review.appliedStatus}${result.review.appliedSchool ? ` — ${result.review.appliedSchool}` : ""}`
              : "Dismissed — the profile was left as it was."}
          </p>
        )}
      </div>
    </div>
  );
};
